import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, ScanLine, Boxes, LayoutDashboard, Users, Tags, ShieldCheck, Database, Settings, ClipboardList, History, BarChart3, FlaskConical, Truck, PackageOpen, Warehouse, LogOut } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { LangToggle } from "./LangToggle";
import { SyncBadge, useSyncDriver, useSyncState } from "./SyncBadge";

const NAV = {
  packing_house: [["/incoming", "nav_incoming", PackageOpen], ["/scan?mode=ph", "nav_scan", ScanLine], ["/bins", "nav_bins", Boxes], ["/dashboard", "nav_dashboard", LayoutDashboard], ["/reports", "nav_reports", BarChart3]],
  supervisor: [["/home", "nav_home", Home], ["/scan", "nav_scan", ScanLine], ["/bins", "nav_bins", Boxes]],
  farm_manager: [["/dashboard", "nav_dashboard", LayoutDashboard], ["/reports", "nav_reports", BarChart3], ["/scan?mode=dock", "nav_dock", Warehouse], ["/dispatch", "nav_dispatch", Truck], ["/bins", "nav_bins", Boxes],
    ["/attendance", "nav_attendance", ClipboardList], ["/labels", "nav_labels", Tags], ["/lab", "nav_lab", FlaskConical]],
  exporter: [["/dashboard", "nav_dashboard", LayoutDashboard], ["/reports", "nav_reports", BarChart3], ["/dispatch", "nav_dispatch", Truck], ["/incoming", "nav_incoming", PackageOpen], ["/bins", "nav_bins", Boxes], ["/labels", "nav_labels", Tags],
    ["/releases", "nav_releases", ShieldCheck], ["/lab", "nav_lab", FlaskConical], ["/scan?mode=dock", "nav_scan", ScanLine]],
  admin: [["/dashboard", "nav_dashboard", LayoutDashboard], ["/reports", "nav_reports", BarChart3], ["/scan?mode=dock", "nav_scan", ScanLine], ["/dispatch", "nav_dispatch", Truck], ["/incoming", "nav_incoming", PackageOpen], ["/bins", "nav_bins", Boxes],
    ["/attendance", "nav_attendance", ClipboardList], ["/labels", "nav_labels", Tags], ["/releases", "nav_releases", ShieldCheck], ["/lab", "nav_lab", FlaskConical],
    ["/admin/users", "nav_users", Users], ["/admin/logins", "nav_logins", History], ["/admin/master", "nav_master", Database], ["/admin/settings", "nav_settings", Settings]],
};

export const Wordmark = ({ light, className = "h-8" }) => (
  <img src={light ? "/logo-white.png" : "/logo.png"} alt="365Fresh" className={`${className} w-auto max-w-full self-start object-contain select-none`} data-testid="wordmark" />
);

export default function AppShell() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const items = NAV[user?.role] || [];
  const field = ["supervisor", "farm_manager", "admin"].includes(user?.role);
  useSyncDriver(field);
  const sync = useSyncState();
  const doLogout = () => {
    if (sync.pending + sync.conflicts.length > 0 && !window.confirm(t("logout_pending_warning"))) return;
    logout(); nav("/login");
  };
  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-30 bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-4">
          <img src="/logo-white.png" alt="365Fresh" className="h-7 sm:h-8 w-auto object-contain select-none" data-testid="wordmark" />
          <span className="hidden sm:inline-block border-l border-slate-600 pl-4 text-xs font-bold uppercase tracking-wider text-slate-300" data-testid="header-app-name">{t("app_name")}</span>
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden md:block text-right leading-tight">
              <span className="block text-sm font-semibold" data-testid="header-user-name">{user?.full_name}</span>
              <span className="block text-xs text-slate-400">{t(`role_${user?.role}`)}</span>
            </span>
            {field && <SyncBadge />}
            <LangToggle dark />
            <button data-testid="logout-button" onClick={doLogout} className="p-2 rounded-lg hover:bg-white/10" aria-label={t("logout")}>
              <LogOut size={20} />
            </button>
          </div>
        </div>
        <nav className="bg-[#800B28] overflow-x-auto">
          <div className="max-w-7xl mx-auto px-2 flex">
            {items.map(([to, key, Icon]) => (
              <NavLink key={to} to={to} data-testid={`nav-${key}`} end
                className={({ isActive }) => `flex items-center gap-2 px-2 xl:px-2.5 2xl:px-3 py-3 text-sm lg:text-[13px] 2xl:text-sm font-semibold whitespace-nowrap border-b-4 transition-colors ${isActive ? "border-white text-white" : "border-transparent text-rose-100 hover:text-white"}`}>
                <Icon size={18} className="lg:hidden 2xl:block" /> {t(key)}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>
      <main className="flex-1 w-full max-w-7xl mx-auto p-4 md:p-6 fade-in">
        <Outlet />
      </main>
    </div>
  );
}
